import React, { useEffect, useState } from 'react'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import { Card, Stack } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useToggle } from 'react-use'
import type { ReactNode } from 'react'
import type { PivotLayout, PivotLayoutField, PivotLayoutSwitchTo, PivotMeasureFormat } from '@ztechapps/common.contract'
import { PivotLayoutProblemEnum, PivotNegativeNumberFormatEnum } from '@ztechapps/common.contract'
import {
  describePivotFilter,
  describePivotPlacement,
  describePivotValueFilter,
  isPivotCalculatedMeasure,
  validatePivotLayout,
} from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { showAllFilterTemplate } from '@ztechapps/common.hook'
import { useOpenPivotContext, toPivotMeasureLabel } from '@ztechapps/webui.reporting'
import { Alert, HorizontalStack, Icon, ShowIf } from '@ztechapps/webui.util'

export type BuildPivotLayoutProps = {
  /** The report the layout was built for; `path` is the admin route that opens it. */
  report: { name: string; title: string; path: string }
  layout: PivotLayout
  note?: string
  /** Present when the range sits outside the loaded rows — the exact header filter name plus the required days. */
  switchTo?: PivotLayoutSwitchTo
}

// The header-filter menu's reload-all pseudo-template carries an empty value, so switching to it
// refetches every row — the card confirm-gates that target before running.

// One bounded wait per click for the switched page's refetch to land in the live loaded days.
const SWITCH_APPLY_TIMEOUT_MS = 30000

const describeFields = (fields: PivotLayoutField[]): string =>
  isEmpty(fields) ? 'None' : fields.map((item) => describePivotPlacement(item)).join(', ')

const describeFormat = (format?: PivotMeasureFormat): string =>
  compact([
    format?.currencySymbol && `${format.currencySymbol} currency`,
    format?.decimalPlaces !== undefined && `${format.decimalPlaces} decimals`,
    format?.isPercent && 'percent',
    format?.negativeNumberFormat === PivotNegativeNumberFormatEnum.Parentheses && 'negatives in ( )',
  ]).join(', ')

type SummaryLineProps = { label: string; children: ReactNode }

const SummaryLine = ({ label, children }: SummaryLineProps) => (
  <HorizontalStack gap={2} className="align-items-start small">
    <span className="fw-semibold flex-shrink-0" style={{ width: '4.5rem' }}>
      {label}
    </span>
    <div className="flex-grow-1 text-break">{children}</div>
  </HorizontalStack>
)

/**
 * Renders the layout the `buildPivotLayout` tool built: what each pivot zone would hold, a short note,
 * and the control that applies it to the open pivot, undoes that, or opens the report it was built for.
 * A card carrying a header-filter switch resolves that exact name through the page's own filter chain,
 * waits for the live loaded days to cover the required range and then applies — a reload-all target
 * confirm-gates first, a timed-out wait fails without retrying. The card sends nothing to chat.
 */
export const BuildPivotLayout = ({ report, layout, note, switchTo }: BuildPivotLayoutProps) => {
  const { openPivot } = useOpenPivotContext()
  const [isApplied, setIsApplied] = useToggle(false)
  const [previousLayout, setPreviousLayout] = useState<PivotLayout | undefined>(undefined)
  const [confirmingSwitch, setConfirmingSwitch] = useToggle(false)
  const [isSwitching, setIsSwitching] = useToggle(false)
  const [switchError, setSwitchError] = useState<string | undefined>(undefined)
  const isReportOpen = openPivot?.report === report.name

  // The pivot starts again from its default layout when its page is left, so nothing stays applied —
  // and a switch left waiting on another page's days stops instead of applying there.
  useEffect(() => {
    if (!isReportOpen) {
      setIsApplied(false)
      setConfirmingSwitch(false)
      setIsSwitching(false)
      setSwitchError(undefined)
    }
  }, [isReportOpen, setIsApplied, setConfirmingSwitch, setIsSwitching, setSwitchError])

  // While a switch is in flight the provider re-registers on every loaded-days change, so this
  // re-checks the carried layout against the live days each time — covered means the validator's
  // own range rule passes, and the apply below runs once before the flag flips.
  useEffect(() => {
    if (!isSwitching || !isReportOpen || !openPivot || !switchTo || isEmpty(openPivot.fields)) return
    const problems = validatePivotLayout({ layout, fields: openPivot.fields, loadedDays: openPivot.loadedDays })
    const covered = !problems.some((problem) => problem.code === PivotLayoutProblemEnum.OutsideLoadedDays)
    if (!covered) return
    openPivot.apply({ ...layout, chartType: openPivot.layout.chartType })
    setIsSwitching(false)
    setIsApplied(true)
  }, [isSwitching, isReportOpen, openPivot, switchTo, layout, setIsSwitching, setIsApplied])

  // One bounded wait per click — expiry fails the card, with no second switch and no retry of its own.
  useEffect(() => {
    if (!isSwitching) return
    const timer = setTimeout(() => {
      setIsSwitching(false)
      setSwitchError(
        switchTo
          ? `Could not load ${switchTo.requiredRange.from} to ${switchTo.requiredRange.to} from ${switchTo.headerFilter} in time. Try switching again.`
          : 'The switched rows did not load in time. Try switching again.'
      )
    }, SWITCH_APPLY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isSwitching, switchTo, setIsSwitching, setSwitchError])

  const handleApply = () => {
    if (!openPivot) return
    setPreviousLayout(openPivot.layout)
    openPivot.apply({ ...layout, chartType: openPivot.layout.chartType })
    setIsApplied(true)
  }

  const handleUndo = () => {
    if (!openPivot || !previousLayout) return
    openPivot.apply(previousLayout)
    setIsApplied(false)
  }

  // One click runs one execution: the exact name goes through the page's filter chain a single
  // time, then the coverage effect above waits on the live loaded days before applying.
  const startSwitchExecution = () => {
    if (!openPivot || !switchTo || isSwitching) return
    // An unknown name no-ops through the page's filter chain with zero requests, so it lands
    // in error here instead of burning the bounded wait below. The reload-all target is not a
    // saved name but still rides that chain, so it skips the membership check.
    if (
      switchTo.headerFilter !== showAllFilterTemplate.name &&
      !openPivot.headerFilters.includes(switchTo.headerFilter)
    ) {
      setConfirmingSwitch(false)
      setSwitchError(`No header filter named '${switchTo.headerFilter}'.`)
      return
    }
    setPreviousLayout(openPivot.layout)
    openPivot.switchHeaderFilter?.(switchTo.headerFilter)
    setConfirmingSwitch(false)
    setSwitchError(undefined)
    setIsSwitching(true)
  }

  const handleSwitchClick = () => {
    if (!switchTo) return
    if (switchTo.headerFilter === showAllFilterTemplate.name && !confirmingSwitch) {
      setConfirmingSwitch(true)
      return
    }
    startSwitchExecution()
  }

  const handleCancelSwitch = () => {
    setConfirmingSwitch(false)
  }

  const switchTargetLine = switchTo ? (
    <div className="small text-body-secondary">
      Switch to {switchTo.headerFilter} for {switchTo.requiredRange.from} to {switchTo.requiredRange.to}.
    </div>
  ) : null

  let footer: ReactNode = (
    <BusyButton variant="primary" bsSize="sm" icon="check" text="Apply to pivot" onClick={handleApply} />
  )
  if (!isReportOpen) {
    footer = (
      <Link to={report.path} className="btn btn-outline-primary btn-sm">
        {`Open ${report.title} to apply`}
      </Link>
    )
  } else if (isApplied) {
    footer = (
      <HorizontalStack gap={2}>
        <span className="small text-success-emphasis">
          <Icon name="circle-check" aria-hidden="true" /> Applied to the pivot
        </span>
        <BusyButton
          variant="outline-secondary"
          bsSize="sm"
          icon="rotate-left"
          text="Undo"
          title="Restore the previous pivot layout"
          className="ms-auto"
          onClick={handleUndo}
        />
      </HorizontalStack>
    )
  } else if (switchError !== undefined) {
    footer = (
      <Stack gap={2}>
        <Alert type="danger">
          <span className="text-body small">{switchError}</span>
        </Alert>
        <HorizontalStack gap={2}>
          <BusyButton
            variant="primary"
            bsSize="sm"
            icon="check"
            text="Switch and apply"
            onClick={startSwitchExecution}
          />
        </HorizontalStack>
      </Stack>
    )
  } else if (confirmingSwitch && switchTo) {
    footer = (
      <Stack gap={2}>
        <div className="small text-body-secondary">Switching to {switchTo.headerFilter} reloads all rows.</div>
        <HorizontalStack gap={2}>
          <BusyButton
            variant="primary"
            bsSize="sm"
            icon="check"
            text="Switch and apply"
            onClick={startSwitchExecution}
          />
          <BusyButton variant="outline-secondary" bsSize="sm" text="Cancel" onClick={handleCancelSwitch} />
        </HorizontalStack>
      </Stack>
    )
  } else if (switchTo) {
    footer = (
      <Stack gap={2}>
        {switchTargetLine}
        <div>
          <BusyButton
            variant="primary"
            bsSize="sm"
            icon="check"
            text="Switch and apply"
            busyText="Switching…"
            isBusy={isSwitching}
            onClick={handleSwitchClick}
          />
        </div>
      </Stack>
    )
  }

  return (
    <Card className="bg-body">
      <Card.Header>
        <HorizontalStack gap={2}>
          <Icon name="table-cells" className="text-secondary" aria-hidden="true" />
          <span className="fw-semibold">Pivot layout</span>
          <span className="ms-auto small text-body-secondary">{report.title}</span>
        </HorizontalStack>
      </Card.Header>
      <Card.Body className="p-2">
        <Stack gap={1}>
          <SummaryLine label="Rows">{describeFields(layout.rows)}</SummaryLine>
          <SummaryLine label="Columns">{describeFields(layout.columns)}</SummaryLine>
          <SummaryLine label="Measures">
            <Stack gap={1}>
              {layout.measures.map((measure) => (
                <div key={`${measure.field}|${isPivotCalculatedMeasure(measure) ? 'calculated' : measure.aggregation}`}>
                  {isPivotCalculatedMeasure(measure) ? (
                    <>
                      <span className="fw-semibold">{measure.field}</span> <code>{measure.formula}</code>{' '}
                      <span className="text-body-secondary">calculated</span>
                    </>
                  ) : (
                    <span className="fw-semibold">{toPivotMeasureLabel(measure)}</span>
                  )}
                  <ShowIf condition={!!measure.format}>
                    <div className="text-body-secondary">{describeFormat(measure.format)}</div>
                  </ShowIf>
                </div>
              ))}
            </Stack>
          </SummaryLine>
          <SummaryLine label="Filters">
            <Stack gap={1}>
              {layout.filters.map((filter) => (
                <div key={`${filter.field}|${filter.dateGroup ?? ''}`}>
                  {filter.valueFilter
                    ? describePivotValueFilter(layout, filter)
                    : describePivotFilter(filter, isReportOpen ? (openPivot?.loadedDays ?? []) : [])}
                </div>
              ))}
            </Stack>
          </SummaryLine>
        </Stack>
        <ShowIf condition={!!note}>
          <div className="small text-body-secondary mt-2">{note}</div>
        </ShowIf>
      </Card.Body>
      <Card.Footer>{footer}</Card.Footer>
    </Card>
  )
}
